/**
 * Background events that grey out the parts of the visible week
 * sitting *outside* the common-availability windows. Gives a clear
 * visual signal for "nobody works here" versus busy pink blocks.
 *
 * When there are no interviewers selected, we emit nothing — the
 * empty state shouldn't look like a wall of grey.
 */
import { useMemo } from 'react';
import type { EventInput } from '@fullcalendar/core';
import {
  buildUnavailableHoursIntervals,
  type Interval,
} from '../../availability';
import { EVENT_CATEGORIES, EVENT_CLASS_NAMES } from '../constants';

export interface UseUnavailableHoursOverlayArgs {
  availability: Interval[];
  timezone: string;
  visibleWeekStartIso: string;
  visibleWeekEndIso: string;
  /** When false (no interviewers, self-schedule mode, …) we emit nothing. */
  enabled: boolean;
}

export function useUnavailableHoursOverlay({
  availability,
  timezone,
  visibleWeekStartIso,
  visibleWeekEndIso,
  enabled,
}: UseUnavailableHoursOverlayArgs): EventInput[] {
  return useMemo<EventInput[]>(() => {
    if (!enabled) return [];
    const intervals = buildUnavailableHoursIntervals({
      visibleStartIso: visibleWeekStartIso,
      visibleEndIso: visibleWeekEndIso,
      availability,
      timezone,
    });
    return intervals.map((iv, idx) => ({
      id: `unavailable-${idx}`,
      start: iv.start,
      end: iv.end,
      display: 'background' as const,
      classNames: EVENT_CLASS_NAMES.unavailableHours,
      color: 'transparent',
      extendedProps: { category: EVENT_CATEGORIES.unavailableHours },
    }));
  }, [enabled, availability, timezone, visibleWeekStartIso, visibleWeekEndIso]);
}
